import { SpecialOfferProps } from '../types';
import { Box, Stack } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useEffect, useMemo, useRef, useState } from 'react';
import { EProductPriority } from 'utils/enums';
import {
  StyledPriceSection,
  StyledProductMadnessSpecialOffer,
  StyledScrollerButton,
  StyledSubItems,
} from './style';
import { ProductMadnessSpecialOfferSubProduct as SubProduct } from './Products/sub';
import { ProductMadnessSpecialOfferPrice as Price } from './Price';
import { moveProductGrid } from 'utils';
import { ProductData } from 'utils/types';
import { ProductMadnessOfferSection as OfferSection } from './Offer';

export const ProductMadnessSpecialOffer: React.FC<SpecialOfferProps> = ({
  price,
  backgroundColor = {
    colorOne: '#FFFFFF',
  },
  backgroundImage = 'https://media-staging.appcharge.com/media/660148877b8ae986b7b6379e/1726130855000__70f02c58-e326-44cf-8418-8031376aca79',
  productsList,
  buttonTextColor = '#FFFFFF',
  text = '',
  textColor = { colorOne: '#F9B907' },
  fontSize = 16,
  inMobileMock = false,
  selectOffer,
  availability,
  availabilityText = 'Available',
  isFree = false,
  collectText = 'Collect',
  timer,
  translations = { includes: 'Includes'}
}) => {
  const subItemsGrid = useRef<HTMLDivElement>(null);
  const [isDoneScrolling, setIsDoneScrolling] = useState(false);
  const [showScroller, setShowScroller] = useState(false);
  const [mainProduct, setMainProduct] = useState<ProductData | null>(null);
  const [subProducts, setSubProducts] = useState<ProductData[]>([]);

  useEffect(() => {
    if (!productsList || productsList.length === 0) return;

    const main = productsList.find(
      (product) => product.priority === EProductPriority.MAIN,
    );
    setMainProduct(main!);
    const arrSubProducts = productsList.filter(
      (product) => product.priority === EProductPriority.SUB,
    );
    setSubProducts(arrSubProducts);
  }, [productsList]);

  const handleScrollerClick = (
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e?.stopPropagation();
    const subItemsGridElement = subItemsGrid?.current;
    if (subItemsGridElement as HTMLDivElement) {
      moveProductGrid({
        subItemsGridElement: subItemsGridElement as HTMLDivElement,
        isDoneScrolling,
        setIsDoneScrolling,
      });
    }
  };

  const availabilityDisplayText = useMemo(
    () =>
      availability
        ? `${availabilityText} ${availability.remaining}/${availability.total}`
        : '',
    [availability],
  );

  useEffect(() => {
    let timeoutId: any;

    const shouldShowScrollbar = () => {
      setShowScroller(
        subItemsGrid?.current
          ? subItemsGrid?.current?.scrollWidth >
              subItemsGrid?.current?.clientWidth
          : false,
      );
    };

    shouldShowScrollbar();

    const handleResize = () => {
      clearTimeout(timeoutId);
      shouldShowScrollbar();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [subItemsGrid.current, subProducts]);

  return (
    <StyledProductMadnessSpecialOffer
      backgroundSrc={backgroundImage}
      backgroundColor={backgroundColor}
      onClick={selectOffer}
    >
      <OfferSection
        text={text}
        textColor={textColor}
        fontSize={fontSize}
        mainProduct={mainProduct}
        top={availabilityDisplayText ? 10 : 20}
      >
        <StyledPriceSection gap={'4px'}>
          <Price
            price={price}
            isPreview={inMobileMock}
            buttonTextColor={buttonTextColor}
            isFree={isFree}
            collectText={collectText}
          />
          <Stack direction="row" spacing={2} justifyContent="center">
            {availabilityDisplayText && (
              <div className="availability">{availabilityDisplayText}</div>
            )}
            {timer && (
              <div className="timer">Ends in {timer}</div>
            )}
          </Stack>
        </StyledPriceSection>
      </OfferSection>
      {subProducts.length > 0 && (
        <StyledSubItems gap={'8px'}>
          <span className={'prefix-text'}>{translations.includes}</span>
          <Box className={'grid'} ref={subItemsGrid} gap={'8px'}>
            {subProducts.map(
              ({ quantityDisplay, textFontColorHex, images }, i) => (
                <Stack justifyContent="center" alignItems="center">
                  <SubProduct
                    key={i}
                    quantityDisplay={quantityDisplay}
                    textFontColorHex={textFontColorHex}
                    images={images}
                  />
                </Stack>
              ),
            )}
          </Box>
          {showScroller && (
            <Stack>
              <StyledScrollerButton onClick={handleScrollerClick}>
                {isDoneScrolling ? <ArrowBack /> : <ArrowForward />}
              </StyledScrollerButton>
            </Stack>
          )}
        </StyledSubItems>
      )}
    </StyledProductMadnessSpecialOffer>
  );
};

const ArrowBack: React.FC = () => (
  <ArrowBackIosNewIcon
    sx={{
      color: 'black',
      fontSize: 12,
    }}
  />
);

const ArrowForward: React.FC = () => (
  <ArrowForwardIosIcon
    sx={{
      color: 'black',
      fontSize: 12,
    }}
  />
);
